import { useCallback } from 'react';

import {
  useBatchArchiveEncounterTypesMutation,
  useCleanupApplyMutation,
  useRenameEncounterTypeMutation,
} from '../store/api';
import type { CleanupApplyPlan } from '../types';

export interface CleanupApplyResult {
  archivedCount: number;
  restoredCount: number;
  /** Personal-copy pairs promoted (type + template). */
  promotedCount: number;
  /** Org→org merges applied. */
  mergedCount: number;
  /** Set-aside types renamed. */
  renamedCount: number;
  /** Freed user templates archived. */
  freedTemplateCount: number;
  /** Rows the server declined (still referenced, already archived, etc.). */
  skippedCount: number;
  /** Human labels of steps that errored — empty means a clean run. */
  failedSteps: string[];
}

const EMPTY: Omit<CleanupApplyResult, 'failedSteps'> = {
  archivedCount: 0,
  restoredCount: 0,
  promotedCount: 0,
  mergedCount: 0,
  renamedCount: 0,
  freedTemplateCount: 0,
  skippedCount: 0,
};

/**
 * Fans an apply plan out to the backend verbs (GDN-2295), in order:
 * cleanup-apply (promote/merge) → batch-archive (archives, restores, and the
 * templates freed by those merges) → set-aside renames.
 *
 * Never throws: each step is caught independently and its failure recorded in
 * `failedSteps`, so a partial run reports exactly what landed and what didn't.
 * Every verb is idempotent server-side, so the whole plan is safe to re-apply.
 * Freed-template archives are held back if the merge step failed (nothing was
 * actually freed).
 */
export const useCleanupApply = (
  orgId?: string
): ((plan: CleanupApplyPlan) => Promise<CleanupApplyResult>) => {
  const [cleanupApply] = useCleanupApplyMutation();
  const [batchArchive] = useBatchArchiveEncounterTypesMutation();
  const [renameType] = useRenameEncounterTypeMutation();

  return useCallback(
    async (plan: CleanupApplyPlan): Promise<CleanupApplyResult> => {
      const result: CleanupApplyResult = { ...EMPTY, failedSteps: [] };
      if (!orgId) return result;

      // 1. Promote / merge.
      let mergesApplied = true;
      if (plan.pairPromotions.length || plan.orgMerges.length) {
        try {
          const res = await cleanupApply({
            orgId,
            body: {
              pair_promotions: plan.pairPromotions,
              org_merges: plan.orgMerges,
            },
          }).unwrap();
          result.promotedCount = res.promoted_pairs.length;
          result.mergedCount = res.merged_groups.length;
          result.skippedCount += res.skipped.length;
        } catch {
          mergesApplied = false;
          result.failedSteps.push('merges & promotions');
        }
      }

      // 2. Archives + restores + freed templates (freed only if merges landed).
      const templateIds = mergesApplied ? plan.archiveTemplateIds : [];
      if (
        plan.archiveTypeIds.length ||
        plan.restoreTypeIds.length ||
        templateIds.length
      ) {
        try {
          const res = await batchArchive({
            orgId,
            body: {
              archive_type_ids: plan.archiveTypeIds,
              restore_type_ids: plan.restoreTypeIds,
              archive_template_ids: templateIds,
            },
          }).unwrap();
          result.archivedCount = res.archived_type_ids.length;
          result.restoredCount = res.restored_type_ids.length;
          result.freedTemplateCount = res.archived_template_ids.length;
          result.skippedCount +=
            res.skipped_types.length + res.skipped_templates.length;
        } catch {
          result.failedSteps.push('archives & restores');
        }
      }

      // 3. Set-aside renames (independent PATCHes).
      if (plan.renames.length) {
        const outcomes = await Promise.allSettled(
          plan.renames.map((r) =>
            renameType({ orgId, typeId: r.typeId, name: r.name }).unwrap()
          )
        );
        result.renamedCount = outcomes.filter(
          (o) => o.status === 'fulfilled'
        ).length;
        if (outcomes.some((o) => o.status === 'rejected')) {
          result.failedSteps.push('renames');
        }
      }

      return result;
    },
    [orgId, cleanupApply, batchArchive, renameType]
  );
};
